/* eslint-disable no-plusplus */
import React, {useEffect, useState} from 'react';
import {addDays, isAfter, isBefore, isEqual} from 'date-fns';
import {useWindowDimensions} from 'presentation/hooks';
import {stringfyDate} from 'presentation/utils';
import {
	COMPARISON_OPERATOR_ENUM,
	CompareDateWithCurrentProps,
	DatePickerProps,
} from './DatePicker.types';

import {Day} from './components/Day';
import {DatePickerLayout} from './layout';

const DatePicker: React.FC<DatePickerProps> = ({
	visible,
	showPrevMonthDay,
	showNextMonthDay,
	showNextMonth,
	hoverType,
	includeDateIntervals,
	lang,
	disabledButtonPrevMonth,
	disabledButtonNextMonth,
	pinDayData,
	iconPinDay,
	selectIconPinDay,
	colorPinDay,
	captionCalendar,
	handleChangeDate,
	typeTextWeekDay,
	showBackgroundIntervalWhenHover,
	enableSecondDaySelection,
	onCloseDatePicker,
	firstDaySelected,
	secondDaySelected,
	initialCalendarDate,
	onSelectFirstDay,
	onSelectSecondDay,
	handleChangeDateMobile,
	footerContent,
	availableDates,
	isSingleSelection,
	...restDatePicker
}) => {
	const [yearState, setYearState] = useState(new Date().getFullYear());
	const [monthState, setMonthState] = useState(new Date().getMonth() + 1);

	const [dayHoveredState, setDayHoveredState] = useState<Date>();

	const monthFirstCalendar = monthState - 1;
	const yearFirstCalendar = yearState;

	const monthSecondCalendar = monthState + 1 > 12 ? 0 : monthState;
	const yearSecondCalendar = monthState + 1 > 12 ? yearState + 1 : yearState;

	const {width} = useWindowDimensions();
	const isMobileWidthDimension = width < 992;

	const handlePrevMonth = () => {
		const isJanuary = monthState === 1;
		if (!isJanuary) {
			setMonthState(monthState - 1);
			return;
		}
		setYearState(yearState - 1);
		setMonthState(12);
	};

	const handleNextMonth = () => {
		const isDecember = monthState === 12;
		if (!isDecember) {
			setMonthState(monthState + 1);
			return;
		}
		setYearState(yearState + 1);
		setMonthState(1);
	};

	const handleClickDay = (day: Date) => {
		if (!firstDaySelected) {
			onSelectFirstDay(day);
			handleChangeDate(day, null);

			if (handleChangeDateMobile) {
				handleChangeDateMobile(day, null);
			}
			return;
		}

		if (day.getTime() < firstDaySelected.getTime()) {
			onSelectFirstDay(day);
			handleChangeDate(day, null);

			if (handleChangeDateMobile) {
				handleChangeDateMobile(day, null);
			}
			return;
		}

		if (firstDaySelected.getTime() === day.getTime()) {
			if (isSingleSelection) {
				return;
			}

			onSelectFirstDay(undefined);
			if (secondDaySelected && onSelectSecondDay) {
				handleChangeDate(secondDaySelected, null);

				onSelectFirstDay(secondDaySelected);
				onSelectSecondDay(undefined);
			}
			return;
		}

		if (firstDaySelected && secondDaySelected?.getTime() === day.getTime()) {
			if (onSelectSecondDay) {
				onSelectSecondDay(undefined);
			}
			return;
		}

		if (firstDaySelected && !enableSecondDaySelection) {
			onSelectFirstDay(day);
			handleChangeDate(day, null);

			if (handleChangeDateMobile) {
				handleChangeDateMobile(day, null);
			}
			return;
		}
		if (enableSecondDaySelection && firstDaySelected) {
			if (onSelectSecondDay) {
				onSelectSecondDay(day);
			}
			handleChangeDate(firstDaySelected, day);

			if (handleChangeDateMobile) {
				handleChangeDateMobile(firstDaySelected, day);
			}
		}
	};

	const getDaysInMonth = (year: number, month: number) => new Date(year, month, 0).getDate();

	const getFirstDayOfMonth = (year: number, month: number) =>
		new Date(year, month - 1, 1).getDay();

	const getDayOfWeekend = (year: number, month: number, day: number) => {
		const weekDay = new Date(year, month - 1, day).getDay();
		return weekDay;
	};

	const compareDateWithCurrent = ({
		year,
		month,
		day,
		comparisonOperator,
	}: CompareDateWithCurrentProps) => {
		const today = new Date();
		const todayFormatted = new Date(
			today.getFullYear(),
			today.getMonth(),
			today.getDate(),
		).getTime();
		const weekDay = new Date(year, month, day).getTime();

		const RENDER_COMPARE = {
			[COMPARISON_OPERATOR_ENUM.EQUAL]: todayFormatted === weekDay,
			[COMPARISON_OPERATOR_ENUM.LESS_THAN]: todayFormatted < weekDay,
			[COMPARISON_OPERATOR_ENUM.GREATER_THAN]: todayFormatted > weekDay,
			[COMPARISON_OPERATOR_ENUM.LESS_THAN_OR_EQUAL]: todayFormatted <= weekDay,
			[COMPARISON_OPERATOR_ENUM.MORE_THAN_OR_EQUAL]: todayFormatted >= weekDay,
		};

		return RENDER_COMPARE[comparisonOperator];
	};

	const getDayOfThePast = (date: Date) => {
		if (includeDateIntervals) {
			const {start, end} = includeDateIntervals;
			const isWithinRange =
				(isAfter(date, start) || isEqual(date, start)) &&
				(isBefore(date, end) || isEqual(date, end));
			return !isWithinRange;
		}

		if (availableDates) {
			const formatDate = stringfyDate(date);
			const includeDates = availableDates.includes(formatDate);

			return !includeDates;
		}

		const isAfterToday = isAfter(new Date(), addDays(date, 1));
		return isAfterToday;
	};

	const getToday = (date: Date) => {
		const dateToCompare = {
			year: date.getFullYear(),
			month: date.getMonth(),
			day: date.getDate(),
			comparisonOperator: COMPARISON_OPERATOR_ENUM.EQUAL,
		} as CompareDateWithCurrentProps;

		return compareDateWithCurrent(dateToCompare);
	};

	const getLastDayOfMonth = (date: Date) => {
		const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0).getTime();
		return lastDay;
	};

	const PinInDay = (date: Date) => {
		const Pin = pinDayData?.find(pin => {
			const PinDate = pin.date.getTime();
			if (PinDate === date.getTime()) {
				return pin;
			}
			return undefined;
		});

		return Pin;
	};

	const handleHoverDay = (date: Date) => {
		if (showBackgroundIntervalWhenHover) {
			setDayHoveredState(date);
		}
	};

	const renderCalendar = (year: number, month: number) => {
		const daysInMonth = getDaysInMonth(year, month);
		const firstDayOfMonth = getFirstDayOfMonth(year, month);
		const calendar: React.ReactElement[] = [];
		let dayOfWeek = firstDayOfMonth === 0 ? 7 : firstDayOfMonth;

		// Calculate the number of days to render from the previous month
		const daysFromPrevMonth = dayOfWeek === 7 ? 0 : dayOfWeek;

		const prevMonthYear = month === 0 ? year - 1 : year;
		const prevMonth = month === 0 ? 11 : month - 1;
		const daysInPrevMonth = getDaysInMonth(prevMonthYear, prevMonth);

		for (let day = daysInPrevMonth - daysFromPrevMonth + 1; day <= daysInPrevMonth; day++) {
			const weekendDay = getDayOfWeekend(year, month - 1, day);
			calendar.push(
				showPrevMonthDay ? (
					<Day
						key={`prev-day-${day}`}
						day={day}
						inative
						weekendDay={weekendDay}
						hoverType={hoverType}
						onClickDay={() => handleClickDay(new Date(year, month - 1, day))}
					/>
				) : (
					<Day key={`prev-day-${day}`} />
				),
			);
		}

		// Render days of the current month
		for (let day = 1; day <= daysInMonth; day++) {
			const completeDate = new Date(year, month - 1, day);
			const weekendDay = getDayOfWeekend(year, month, day);
			const isToday = getToday(completeDate);
			const isInative = getDayOfThePast(completeDate);
			const isInativeDaysBeforeDaySelected =
				firstDaySelected &&
				enableSecondDaySelection &&
				firstDaySelected.getTime() > completeDate.getTime();

			const isDaySelected =
				firstDaySelected?.getTime() === completeDate.getTime() ||
				secondDaySelected?.getTime() === completeDate.getTime();

			const isIntervalSelected =
				firstDaySelected &&
				secondDaySelected &&
				completeDate.getTime() >= firstDaySelected.getTime() &&
				completeDate.getTime() <= secondDaySelected.getTime();

			const isLastDayOfMonth = completeDate.getTime() === getLastDayOfMonth(completeDate);

			const currentPinDayData = PinInDay(completeDate);
			const showIconPin = Boolean(currentPinDayData?.date);

			const isFirstDaySelected = firstDaySelected?.getTime() === completeDate.getTime();
			const isSecondDaySelected = secondDaySelected?.getTime() === completeDate.getTime();

			const isFirstDayLessThanDayHovered =
				firstDaySelected &&
				dayHoveredState &&
				firstDaySelected?.getTime() < dayHoveredState?.getTime();

			const isCurrentDateHoveredMoreThanCompleteDate =
				dayHoveredState && dayHoveredState.getTime() > completeDate.getTime();

			const showBackgroundIntervalHovered =
				isFirstDayLessThanDayHovered &&
				!isInative &&
				isCurrentDateHoveredMoreThanCompleteDate &&
				!isInativeDaysBeforeDaySelected &&
				!secondDaySelected &&
				showBackgroundIntervalWhenHover;

			const hasFirstDaySelected = Boolean(firstDaySelected?.getDate());
			const hasSecondDaySelected = Boolean(secondDaySelected?.getDate());

			calendar.push(
				<Day
					key={`day-${day}`}
					day={day}
					isToday={isToday}
					inative={isInative}
					weekendDay={weekendDay}
					hoverType={hoverType}
					isDaySelected={isDaySelected}
					setBackgroundInterval={isIntervalSelected || showBackgroundIntervalHovered}
					isFirstDaySelected={isFirstDaySelected}
					isSecondDaySelected={isSecondDaySelected}
					isLastDayOfMonth={isLastDayOfMonth}
					tooltipData={currentPinDayData?.value}
					iconPinDay={showIconPin && !isInative ? iconPinDay : undefined}
					selectIconPinDay={showIconPin && !isInative ? selectIconPinDay : iconPinDay}
					colorPinDay={showIconPin ? colorPinDay : undefined}
					onClickDay={() => handleClickDay(new Date(year, month - 1, day))}
					onMouseEnter={() => handleHoverDay(completeDate)}
					hasFirstDaySelected={hasFirstDaySelected}
					hasSecondDaySelected={hasSecondDaySelected}
					showBackgroundIntervalWhenHover={showBackgroundIntervalWhenHover}
				/>,
			);
			dayOfWeek = (dayOfWeek % 7) + 1;
		}

		// Determine the number of days to render from the next month
		const daysFromNextMonth = 7 - dayOfWeek;
		for (let day = 1; day <= daysFromNextMonth; day++) {
			const completeDate = new Date(year, month - 1, day);
			const weekendDay = getDayOfWeekend(year, month + 1, day);
			const currentPinDayData = PinInDay(completeDate);
			const showIconPin = Boolean(currentPinDayData?.date);

			calendar.push(
				showNextMonthDay ? (
					<Day
						key={`next-day-${day}`}
						day={day}
						inative
						isDisableColor={true}
						weekendDay={weekendDay}
						hoverType={hoverType}
						isFirstDaySelected={firstDaySelected?.getTime() === completeDate.getTime()}
						isSecondDaySelected={
							secondDaySelected?.getTime() === completeDate.getTime()
						}
						tooltipData={currentPinDayData?.value}
						iconPinDay={showIconPin ? iconPinDay : undefined}
						colorPinDay={showIconPin ? colorPinDay : undefined}
						onClickDay={() => handleClickDay(new Date(year, month, day))}
					/>
				) : (
					<Day key={`next-day-${day}`} />
				),
			);
		}
		return calendar;
	};

	useEffect(() => {
		if (!visible) {
			return;
		}

		if (firstDaySelected) {
			setYearState(firstDaySelected.getFullYear());
			setMonthState(firstDaySelected.getMonth() + 1);
			return;
		}

		if (secondDaySelected) {
			setYearState(secondDaySelected.getFullYear());
			setMonthState(secondDaySelected.getMonth() + 1);
			return;
		}

		if (initialCalendarDate) {
			setYearState(initialCalendarDate.getFullYear());
			setMonthState(initialCalendarDate.getMonth() + 1);
			return;
		}

		const today = new Date();
		setYearState(today.getFullYear());
		setMonthState(today.getMonth() + 1);
	}, [visible, firstDaySelected, secondDaySelected, initialCalendarDate]);

	return (
		<DatePickerLayout
			visible={visible}
			showNextMonth={showNextMonth}
			isMobileWidthDimension={isMobileWidthDimension}
			handlePrevMonth={handlePrevMonth}
			handleNextMonth={handleNextMonth}
			monthState={monthState}
			yearState={yearState}
			disabledButtonPrevMonth={disabledButtonPrevMonth}
			disabledButtonNextMonth={disabledButtonNextMonth}
			lang={lang}
			monthFirstCalendar={monthFirstCalendar}
			yearFirstCalendar={yearFirstCalendar}
			monthSecondCalendar={monthSecondCalendar}
			yearSecondCalendar={yearSecondCalendar}
			typeTextWeekDay={typeTextWeekDay}
			captionCalendar={captionCalendar}
			firstDaySelected={firstDaySelected}
			secondDaySelected={secondDaySelected}
			showLabelSelectedDates={enableSecondDaySelection}
			renderCalendarCurrentMonth={() => renderCalendar(yearState, monthState)}
			renderCalendarNextMonth={() =>
				renderCalendar(yearSecondCalendar, monthSecondCalendar + 1)
			}
			onCloseDatePicker={onCloseDatePicker}
			footerContent={footerContent}
			{...restDatePicker}
		/>
	);
};

export default DatePicker;
